import { Injectable } from '@angular/core'
import { StatusUnavailableError } from '../utils/status-unavailable-error'
import { HumecloudService } from './humecloud.service'
import { LogService, ProjectCMSWidgetType } from 'htv-lib'
import { BehaviorSubject, filter, map, mergeMap, Subject, Subscription } from 'rxjs'
import { RegisterPendingDto, RegisterResponseDto, RegisterSuccessDto } from '../interfaces/dto/register-dto'
import { StatusFailureDto, StatusResponseDto, StatusSuccessDto } from '../interfaces/dto/status-response-dto'
import { ChannelService } from './channel.service'
import { StorageService } from './storage.service'
import { MediaService } from './media.service'
import { MonitoringService } from './monitoring.service'
import { VolumeService } from './volume.service'
import { SmartAppService } from './smart-app.service'
import { LanguageService } from './language.service'
import { ProjectService } from './project.service'
import { EpgService } from './epg.service'
import { CastService } from './cast.service'
import { PowerState } from '../interfaces/power-state'
import { LocationService } from './location.service'
import { HTVAction, HTVActionType } from '../interfaces/dto/monitoring-response-dto'
import { PlatformService } from '../platforms/platform.service'
import { NetworkService } from './network.service'
import { RouterService } from './router.service'
import { OccupancyStatus } from '../interfaces/location'
import { StateDataService } from './state-data.service'
import { environment } from '../environments/environment'
import { isEqual } from 'lodash'
import { VODService } from './vod.service'
import { LoaderService } from './loader.service'
import { AutoRebootService } from './auto-reboot.service'

@Injectable({
  providedIn: 'root',
})
export class StateService {
  isLoading$ = new BehaviorSubject<boolean>(true)
  isRegistered$ = new BehaviorSubject<boolean>(false)
  showingWidgets$ = new BehaviorSubject<Set<ProjectCMSWidgetType>>(new Set())
  widgetAction$ = new Subject<{ widget: ProjectCMSWidgetType; action: string }>()

  error$ = new BehaviorSubject<any>(undefined)

  powerState?: PowerState
  powerStateSubscription?: Subscription

  occupancyStatus?: OccupancyStatus
  occupancyStatusSubscription?: Subscription
  presenceStatusSubscription?: Subscription

  applicationFocus = true
  applicationFocusSubject = new BehaviorSubject<boolean>(true)
  applicationFocusSubscription?: Subscription

  status?: StatusSuccessDto

  code?: string
  expireAt?: Date
  deviceId?: string
  deviceName?: string
  customerId?: string

  stateTimer: any
  readonly stateTimerDelay = 60000

  isCheckStatusRunning = false

  readonly statusKey = 'status'
  readonly registerKey = 'register'

  disablePowerOnReloadFlag = false

  constructor(
    private autoRebootService: AutoRebootService,
    private castService: CastService,
    private channelService: ChannelService,
    private epgService: EpgService,
    private humecloudService: HumecloudService,
    private languageService: LanguageService,
    private loaderService: LoaderService,
    private locationService: LocationService,
    private logService: LogService,
    private mediaService: MediaService,
    private monitoringService: MonitoringService,
    private networkService: NetworkService,
    private platformService: PlatformService,
    private projectService: ProjectService,
    private routerService: RouterService,
    private smartAppService: SmartAppService,
    private stateDataService: StateDataService,
    private storageService: StorageService,
    private vodService: VODService,
    private volumeService: VolumeService
  ) {
    // do nothing
  }

  async init(): Promise<void> {
    this.logService.info(`[StateService] Init`)

    this.isLoading$.next(true)
    this.error$.next(undefined)

    this.registerPowerStateListener()
    this.registerApplicationFocusListener()

    try {
      await this.networkService.waitForConnection()
    } catch (err: any) {
      this.logService.info(`[StateService] Network is down`)
    }

    const isRegistered = await this.checkRegistration()
    this.platformService.afterRegistrationHook(this.deviceId)

    if (isRegistered) {
      let status: StatusSuccessDto | undefined
      try {
        this.loaderService.setDebugStep('PAGES.LOADER.RETRIEVING_STATUS')
        status = await this.retrieveRemoteStatus()
      } catch (err: any) {
        status = this.retrieveLocalStatus()
      }
      this.loaderService.clearStep()

      if (status) {
        await this.loadStatus(status)
      } else {
        throw new StatusUnavailableError('[StateService] Could not load status')
      }
    } else {
      // Nothing to do, will be redirected to register page by the registerGuard
      this.logService.error(`[StateService] Could not register TV`)
    }

    this.setupStateController()

    this.registerOccupancyStatusListener()
    this.registerPresenceStatusListener()

    this.loaderService.clearStep()
    this.isLoading$.next(false)
  }

  async checkStatusAndReload(reload: boolean): Promise<void> {
    this.logService.info(`[StateService] Checking status and reload (reload=${reload})`)
    if (!this.isCheckStatusRunning) {
      this.isCheckStatusRunning = true

      let status: StatusSuccessDto | undefined
      try {
        status = await this.retrieveRemoteStatus()
      } catch (err: any) {
        status = undefined
      }

      if (status) {
        try {
          const requireReload = !isEqual(this.status, status)
          if (requireReload) {
            await this.loadStatus(status)

            if (reload) {
              this.routerService.reloadToHome()
            }
          }
        } catch (err: any) {
          this.logService.error(`[StateService] Could not update status`)
        }
      }

      this.isCheckStatusRunning = false
    } else {
      this.logService.debug(`[StateService] Status check is already running`)
    }
  }

  checkState(): void {
    if (this.isRegistered$.value) {
      this.sendMonitoring()
    }
  }

  setupStateController(): void {
    if (this.stateTimer) {
      clearInterval(this.stateTimer)
    }
    this.checkState()
    this.stateTimer = setInterval(() => {
      this.checkState()
    }, this.stateTimerDelay)
  }

  checkRegistration(code?: string, roomNumber?: string): Promise<boolean> {
    return new Promise(resolve => {
      this.humecloudService.postRegister(code, roomNumber).subscribe({
        next: (res: RegisterResponseDto) => {
          this.logService.debug(JSON.stringify(res))
          const isRegistered = res.registered
          if (isRegistered) {
            const registerSuccess = res as RegisterSuccessDto
            this.code = undefined
            this.expireAt = undefined
            this.deviceId = registerSuccess.deviceId
            this.customerId = registerSuccess.customerId
            this.storageService.saveBoolean(this.registerKey, true)
            this.stateDataService.deviceId = this.deviceId
            this.stateDataService.customerId = this.customerId
          } else {
            const registerPending = res as RegisterPendingDto
            this.code = registerPending.code
            this.expireAt = registerPending.expireAt
            this.deviceId = undefined
            this.customerId = undefined
            this.storageService.saveBoolean(this.registerKey, false)
            this.stateDataService.deviceId = undefined
            this.stateDataService.customerId = undefined
          }
          this.isRegistered$.next(isRegistered)
          this.logService.info(`Registration status: ${isRegistered}`)
          resolve(isRegistered)
        },
        error: () => {
          const isRegisteredLocal = this.storageService.getBoolean(this.registerKey) ?? false
          if (isRegisteredLocal) {
            this.deviceId = this.stateDataService.deviceId
            this.customerId = this.stateDataService.customerId
          }
          this.isRegistered$.next(isRegisteredLocal)
          this.logService.info(`Local registration status: ${isRegisteredLocal}`)
          resolve(isRegisteredLocal)
        },
      })
    })
  }

  retrieveLocalStatus(): StatusSuccessDto | undefined {
    return this.storageService.getJson(this.statusKey)
  }

  retrieveRemoteStatus(): Promise<StatusSuccessDto> {
    this.logService.debug(`[StateService] Retrieving remote status...`)
    return new Promise((resolve, reject) => {
      this.humecloudService.getStatus().subscribe({
        next: (response: StatusResponseDto) => {
          if (response.status) {
            this.logService.info(`[StateService] Successfully retrieved status from Humecloud`)
            const statusSuccess = response as StatusSuccessDto
            resolve(statusSuccess)
          } else {
            const statusFailure = response as StatusFailureDto
            this.logService.error(`[StateService] Invalid status, reason = ${statusFailure.reason}`)
            reject()
          }
        },
        error: () => {
          reject()
        },
      })
    })
  }

  async loadStatus(status: StatusSuccessDto): Promise<void> {
    this.logService.info(`[StateService] Loading app with Humecloud status`)

    this.deviceName = status.deviceName

    if (status.htvConfig) {
      const htvConfig = status.htvConfig

      const enableDebug = environment.enableDebug || htvConfig.enableDebug
      this.logService.init(enableDebug)

      if (htvConfig?.autoReboot) {
        this.autoRebootService.configure(htvConfig.autoReboot)
      }

      const useNativeVolumeDisplay = htvConfig.useNativeVolumeDisplay || false
      this.volumeService.setNativeVolumeDisplay(useNativeVolumeDisplay)
    }

    // Load services with status
    await this.mediaService.init(status.medias)
    await this.smartAppService.init(status.smartAppSet, status.htvConfig)
    this.channelService.init(status.channelPlan)
    this.epgService.init(this.channelService.channels)
    this.vodService.init(status.htvConfig?.vod)
    this.routerService.initFromProject(status.project)
    this.languageService.initFromProject(status.project)
    this.projectService.init(status.project)
    this.locationService.initFromProject(status.location)
    this.castService.init(this.deviceName, status.htvConfig?.googlecast)

    // Save status
    this.status = status
    this.storageService.saveJson(this.statusKey, status)

    await this.platformService.afterStatusHook(status)
  }

  registerPowerStateListener(): void {
    if (this.powerStateSubscription) {
      this.powerStateSubscription?.unsubscribe()
    }
    this.powerStateSubscription = this.monitoringService.powerState.subscribe((powerState: PowerState | undefined) => {
      if (this.powerState === undefined) {
        this.powerState = powerState
        this.logService.info(`[StateService] PowerState is ${this.powerState}`)
      } else {
        if (this.powerState != powerState) {
          this.powerState = powerState
          this.logService.info(`[StateService] PowerState changed to ${this.powerState}`)
          if (this.powerState == PowerState.STANDBY) {
            this.volumeService.init()
            this.routerService.goToHome()
            this.checkStatusAndReload(true)
          } else {
            this.checkStatusAndReload(!this.disablePowerOnReloadFlag)
            this.disablePowerOnReloadFlag = false
          }
        }
      }
    })
  }

  registerApplicationFocusListener(): void {
    if (this.applicationFocusSubscription) {
      this.applicationFocusSubscription?.unsubscribe()
    }
    this.applicationFocusSubscription = this.applicationFocusSubject.subscribe((applicationFocus: boolean) => {
      if (this.applicationFocus != applicationFocus) {
        this.applicationFocus = applicationFocus
        if (this.applicationFocus) {
          this.routerService.reload()
        }
      }
    })
    this.platformService.registerApplicationFocusListener(this.applicationFocusSubject)
  }

  registerOccupancyStatusListener(): void {
    if (this.occupancyStatusSubscription) {
      this.occupancyStatusSubscription?.unsubscribe()
    }
    this.occupancyStatusSubscription = this.locationService.occupancyStatus$.subscribe(
      (occupancyStatus: OccupancyStatus) => {
        const previousStatus = this.occupancyStatus
        if (this.occupancyStatus != occupancyStatus) {
          this.occupancyStatus = occupancyStatus
        }
        if (this.occupancyStatus != previousStatus) {
          switch (this.occupancyStatus) {
            case OccupancyStatus.AVAILABLE:
              this.setGuestMode(false)
              break
            case OccupancyStatus.CHECKED_IN:
              this.setGuestMode(true)
              break
            case OccupancyStatus.CHECKED_OUT:
              this.setGuestMode(false)
              break
            case OccupancyStatus.UNAVAILABLE:
              this.setGuestMode(false)
              break
            case OccupancyStatus.UNKNOWN:
              this.setGuestMode(false)
              break
            default:
              this.setGuestMode(false)
              break
          }
        }
      }
    )
  }

  registerPresenceStatusListener(): void {
    if (this.presenceStatusSubscription) {
      this.presenceStatusSubscription?.unsubscribe()
    }
    this.presenceStatusSubscription = this.locationService.presenceStatus$.subscribe((presenceStatus: boolean) => {
      this.logService.debug(`[StateService] Presence status: ${presenceStatus}`)
      /*
      if (presenceStatus) {
        // Start TV if Checked-In
        if (this.occupancyStatus == OccupancyStatus.CHECKED_IN && !this.stateDataService.checkInState) {
          this.platformService.setPowerState(PowerState.NORMAL)
          this.routerService.goToPage(RouterPage.WELCOME)
        }
      } else {
        // Restart TV if checked-out ?
      }
      */
    })
  }

  setGuestMode(guestMode: boolean): void {
    if (this.stateDataService.guestMode != guestMode) {
      this.stateDataService.guestMode = guestMode

      // Reset state values
      this.stateDataService.checkInState = false
      this.stateDataService.checkOutState = false
    }

    this.logService.info(`[StateService] Guest Mode: ${this.stateDataService.guestMode}`)
    if (this.stateDataService.guestMode) {
      this.enableGuestMode()
    } else {
      this.disableGuestMode()
    }

    this.channelService.determineChannelPlanType(this.stateDataService.guestMode)
  }

  private enableGuestMode(): void {
    try {
      this.platformService.enableGuestMode()
    } catch (err: any) {
      this.logService.error('[StateService] Could not enable guest mode')
    }

    if (!this.stateDataService.checkInState) {
      this.logService.info('[StateService] Running Check-In...')
      this.routerService.reloadToWelcome()
      this.stateDataService.checkInState = true
    }
  }

  private disableGuestMode(): void {
    try {
      this.platformService.disableGuestMode()
    } catch (err: any) {
      this.logService.error('[StateService] Could not disable guest mode')
    }

    if (!this.stateDataService.checkOutState) {
      this.logService.info('[StateService] Running Check-Out...')
      this.clearGuestData()
      this.stateDataService.checkOutState = true

      // Reboot after 3 seconds
      setTimeout(() => {
        this.platformService.reboot()
      }, 3000)
    }
  }

  clearGuestData(): void {
    this.logService.info('[StateService] Clearing guest data')
    this.castService.clearGuestData()
    this.languageService.clearGuestData()
  }

  async resetApp(): Promise<void> {
    this.logService.info('[StateService] Reseting all devices data')
    this.clearGuestData()
    this.storageService.clearValues()
    await this.mediaService.clearMedias()
    this.routerService.restartApp()
  }

  sendMonitoring(): void {
    this.humecloudService.postMonitoring().subscribe(response => {
      if (response) {
        if (response.actions && response.actions.length > 0) {
          this.handleActions(response.actions)
        }
      }
    })
  }

  handleActions(actions: HTVAction[]): void {
    this.logService.debug(`[StateService] actions = ${JSON.stringify(actions)}`)

    const hasAction = (arr: HTVAction[], type: HTVActionType) => arr.some(a => a.type === type)

    if (hasAction(actions, HTVActionType.SEND_LOGS)) {
      this.logService.info('[ACTION] Send logs')
      this.sendLogs()
    }

    if (hasAction(actions, HTVActionType.CLEAR_DATA)) {
      this.logService.info('[ACTION] Clear data')
      this.clearGuestData()
    }

    if (hasAction(actions, HTVActionType.POWER_OFF)) {
      this.logService.info('[ACTION] Power OFF')
      this.platformService.setPowerState(PowerState.STANDBY)
    }

    if (hasAction(actions, HTVActionType.REBOOT)) {
      this.logService.info('[ACTION] Reboot')
      this.platformService.reboot()
    }
  }

  async sendLogs(): Promise<void> {
    this.smartAppService.isLoading$
      .pipe(
        filter(isLoading => !isLoading),
        map(() => this.smartAppService.getAppList()),
        mergeMap(apps => {
          const str = JSON.stringify(apps)
          const blob = new Blob([str])
          const file = new File([blob], 'apps.json')
          const formData = new FormData()
          formData.append('file', file)
          return this.humecloudService.postLogs(formData)
        })
      )
      .subscribe(() => {
        this.logService.info(`Successfully sent apps to backend`)
      })
  }

  startShowingWidget(widgetType: ProjectCMSWidgetType): void {
    const currentSet = this.showingWidgets$.value
    const newSet = new Set(currentSet)
    newSet.add(widgetType)
    this.showingWidgets$.next(newSet)
  }

  stopShowingWidget(widgetType: ProjectCMSWidgetType): void {
    const currentSet = this.showingWidgets$.value
    const newSet = new Set(currentSet)
    newSet.delete(widgetType)
    this.showingWidgets$.next(newSet)
  }

  isShowingWidget(widgetType: ProjectCMSWidgetType): boolean {
    return this.showingWidgets$.value.has(widgetType)
  }

  sendWidgetAction(widget: ProjectCMSWidgetType, action: string): void {
    this.widgetAction$.next({ widget, action })
  }
}
